import { DEFAULT_THEME_BACKGROUND } from '../shared/utils/theme-color.util';

/** Default page size for `GET package/prepaid-wash-books`. */
export const DEFAULT_PREPAID_WASH_BOOKS_PAGE_LIMIT = 10;

/** Query params for prepaid wash books list. */
export interface PrepaidWashBooksListParams {
  status?: boolean;
  page?: number;
  limit?: number;
  /** Server-side search (name, slug, etc.). */
  search?: string;
  /** Filter books available at this location (`GET ...?location_id=`). */
  location_id?: string;
}

/** Pagination block on list responses (`total_pages` from API). */
export interface PrepaidWashBooksPagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

/** Line item on a prepaid wash book list row. */
export interface PrepaidWashBookListLine {
  id: string;
  sort_order: number;
  package_id: string;
  type: string;
  is_add_on: boolean;
  variant_id?: string | null;
  wash_limit: number;
  limit: number;
  expiry: string;
  price: number;
  expires_at: string | null;
  package_name: string;
  variant_name?: string | null;
  single_list_price?: number;
}

/** Prepaid wash book row from `GET package/prepaid-wash-books`. */
export interface PrepaidWashBook {
  id: string;
  user_id: string;
  slug: string;
  name: string;
  status: boolean;
  selling_price: string;
  /** Theme CSS background or legacy `theme-1`…`theme-10` token. */
  theme_color?: string | null;
  number_of_purchased: number;
  total_redeemed: string;
  created_at: string;
  updated_at: string;
  items: PrepaidWashBookListLine[];
}

/** Normalized list result for components. */
export interface PrepaidWashBooksListResult {
  books: PrepaidWashBook[];
  pagination: PrepaidWashBooksPagination;
}

/** Raw HTTP list body includes `pagination` beside `data`. */
export interface PrepaidWashBooksListApiResponse {
  status: boolean;
  status_code?: number;
  message?: string;
  data?: PrepaidWashBook[];
  pagination?: PrepaidWashBooksPagination;
}

export function emptyPrepaidWashBooksPagination(
  page = 1,
  limit = DEFAULT_PREPAID_WASH_BOOKS_PAGE_LIMIT
): PrepaidWashBooksPagination {
  return { page, limit, total: 0, total_pages: 0 };
}

export function formatPrepaidWashBookItemChip(item: PrepaidWashBookListLine): string {
  const count = item.wash_limit ?? item.limit;
  const label =
    item.package_name?.trim() ||
    (item.variant_name ? `${item.variant_name}` : formatPrepaidWashTypeLabel(item.type));
  return `${count} ${label}`;
}

export function formatPrepaidWashTypeLabel(type: string): string {
  switch (type) {
    case 'single_wash':
      return 'Single Wash';
    case 'add_on':
      return 'Add-on';
    case 'detailing':
      return 'Detailing';
    default:
      return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
}

/** Resolves wash type for order-request detail lines (list row may use `type` or `is_add_on`). */
export function resolvePrepaidWashBookLineType(item: PrepaidWashBookListLine): string {
  if (item.type === 'add_on' || item.is_add_on) {
    return 'add_on';
  }
  return item.type || 'single_wash';
}

/** Order-request modal: wash column, e.g. "2 Single Wash Champion". */
export function formatOrderRequestPrepaidWashLine(item: PrepaidWashBookListLine): string {
  const count = item.wash_limit ?? item.limit;
  const typeLabel = formatPrepaidWashTypeLabel(resolvePrepaidWashBookLineType(item));
  const name = item.package_name?.trim() || item.variant_name?.trim() || typeLabel;
  return `${count} ${typeLabel} ${name}`.replace(/\s+/g, ' ').trim();
}

function formatPrepaidOrderRequestExpiryDate(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

function addCalendarMonths(from: Date, months: number): Date {
  const result = new Date(from);
  result.setMonth(result.getMonth() + months);
  return result;
}

/** Order-request modal: expiry column, e.g. "Expires 02 Oct 2026". */
export function formatOrderRequestPrepaidExpiryLine(
  item: PrepaidWashBookListLine,
  referenceDate: Date = new Date()
): string {
  const expiresAt = (item.expires_at ?? '').trim();
  if (expiresAt) {
    const d = new Date(expiresAt);
    if (!Number.isNaN(d.getTime())) {
      return `Expires ${formatPrepaidOrderRequestExpiryDate(d)}`;
    }
  }
  const months = parsePrepaidExpiryMonths(item.expiry);
  if (months !== null) {
    const d = addCalendarMonths(referenceDate, months);
    return `Expires ${formatPrepaidOrderRequestExpiryDate(d)}`;
  }
  const expiry = (item.expiry ?? '').trim();
  if (expiry) {
    return `Expires ${expiry}`;
  }
  return '';
}

/** Checkout summary card for prepaid wash book purchase (create-payment-request). */
export interface PrepaidCheckoutSummaryViewModel {
  title: string;
  validityLine: string;
  vehicleSummary: string | null;
  priceFormatted: string;
  subtotalLabel: string;
}

/** Long date for checkout validity, e.g. "Thu, Dec 20, 2025". */
function formatPrepaidCheckoutPurchaseDateLong(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) {
    return typeof date === 'string' ? date.trim() : '';
  }
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Slug for subtotal label, e.g. "buy-2-get-3" → "Buy 2 Get 3". */
export function humanizePrepaidWashBookSlug(slug: string | null | undefined): string {
  const s = (slug ?? '').trim();
  if (!s) {
    return '';
  }
  return s
    .split(/[-_]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function resolvePrepaidWashBookItemExpiryDate(
  item: PrepaidWashBookListLine,
  referenceDate: Date
): Date | null {
  const expiresAt = (item.expires_at ?? '').trim();
  if (expiresAt) {
    const d = new Date(expiresAt);
    if (!Number.isNaN(d.getTime())) {
      return d;
    }
  }
  const months = parsePrepaidExpiryMonths(item.expiry);
  if (months !== null) {
    return addCalendarMonths(referenceDate, months);
  }
  return null;
}

/** Checkout card validity row: "Thu, Dec 20, 2025 - Valid till: Fri, Dec 21, 2026". */
export function buildPrepaidCheckoutValidityLine(
  book: PrepaidWashBook,
  purchaseDate: Date
): string {
  const items = book.items ?? [];
  let latestExpiry: Date | null = null;
  for (const item of items) {
    const exp = resolvePrepaidWashBookItemExpiryDate(item, purchaseDate);
    if (exp && (!latestExpiry || exp.getTime() > latestExpiry.getTime())) {
      latestExpiry = exp;
    }
  }
  const purchased = formatPrepaidCheckoutPurchaseDateLong(purchaseDate);
  if (!latestExpiry) {
    return purchased;
  }
  return `${purchased} - Valid till: ${formatPrepaidCheckoutPurchaseDateLong(latestExpiry)}`;
}

export function buildPrepaidCheckoutSubtotalLabel(
  book: PrepaidWashBook | null,
  fallbackName?: string | null
): string {
  const short =
    humanizePrepaidWashBookSlug(book?.slug) || (fallbackName ?? book?.name ?? '').trim();
  return short ? `Subtotal (Prepaid-${short})` : 'Subtotal (Prepaid)';
}

export function buildPrepaidCheckoutSummaryViewModel(input: {
  book: PrepaidWashBook | null;
  titleFallback?: string | null;
  purchaseDate: Date;
  vehicleSummary: string | null;
  priceFormatted: string;
}): PrepaidCheckoutSummaryViewModel {
  const title = (input.book?.name ?? input.titleFallback ?? '').trim() || 'Prepaid wash book';
  const validityLine = input.book?.items?.length
    ? buildPrepaidCheckoutValidityLine(input.book, input.purchaseDate)
    : '';
  return {
    title,
    validityLine,
    vehicleSummary: input.vehicleSummary,
    priceFormatted: input.priceFormatted,
    subtotalLabel: buildPrepaidCheckoutSubtotalLabel(input.book, input.titleFallback),
  };
}

export function sumPrepaidBooksRedeemed(books: PrepaidWashBook[]): number {
  return books.reduce((sum, book) => {
    const n = Number(book.total_redeemed);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
}

/** Form wash line item (subset used for API mapping). */
export interface PrepaidWashItemForm {
  wash_type: string | null;
  wash_name: string | null;
  number_of_washes: number | null;
  wash_price: number | null;
  wash_total?: number | null;
  expiry_months: number | null;
}

/** Form payload shape emitted by add-prepaid-modal. */
export interface PrepaidPackageFormData {
  prepaid_package_name: string;
  package_status: boolean;
  theme_color: string;
  items: PrepaidWashItemForm[];
  total_wash_price?: number | null;
  selling_price: number | null;
}

export type PrepaidWashType = 'single_wash' | 'add_on';

export interface PrepaidWashBookItemPayload {
  package_id: string;
  wash_type: PrepaidWashType;
  expiry: string;
  wash_limit: number;
  price?: number;
  variant_id?: string;
}

export interface CreatePrepaidWashBookPayload {
  name: string;
  status?: boolean;
  selling_price: number;
  theme_color?: string;
  items: PrepaidWashBookItemPayload[];
}

/** Same body as create — used for `PATCH package/prepaid-wash-books/{id}`. */
export type UpdatePrepaidWashBookPayload = CreatePrepaidWashBookPayload;

const EMPTY_WASH_ITEM_FORM: PrepaidWashItemForm = {
  wash_type: null,
  wash_name: null,
  number_of_washes: null,
  wash_price: null,
  expiry_months: null,
};

/** Parses API expiry strings such as `"6 months"`. */
export function parsePrepaidExpiryMonths(expiry: string | null | undefined): number | null {
  const match = /^(\d+)\s*months?$/i.exec((expiry ?? '').trim());
  if (!match) {
    return null;
  }
  const months = Number(match[1]);
  return Number.isFinite(months) ? months : null;
}

/** Maps a list/detail row into add-prepaid-modal form values. */
export function mapPrepaidWashBookToFormData(book: PrepaidWashBook): PrepaidPackageFormData {
  const sortedItems = [...(book.items ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const formItems: PrepaidWashItemForm[] = [];

  for (let index = 0; index < 2; index++) {
    const line = sortedItems[index];
    if (!line) {
      formItems.push({ ...EMPTY_WASH_ITEM_FORM });
      continue;
    }

    const washType =
      line.type === 'add_on' || line.is_add_on ? 'add_on' : (line.type as PrepaidWashType);
    let washName = line.package_id;
    if (line.variant_id?.trim()) {
      washName = `${line.package_id}::${line.variant_id.trim()}`;
    }

    formItems.push({
      wash_type: washType,
      wash_name: washName,
      number_of_washes: line.wash_limit ?? line.limit,
      wash_price: line.price ?? null,
      expiry_months: parsePrepaidExpiryMonths(line.expiry),
    });
  }

  const totalWashPrice = formItems.reduce((sum, item) => {
    const count = Number(item.number_of_washes);
    const price = Number(item.wash_price);
    if (!Number.isFinite(count) || !Number.isFinite(price)) {
      return sum;
    }
    return sum + Math.round(count * price * 100) / 100;
  }, 0);

  const sellingPrice = Number(book.selling_price);

  return {
    prepaid_package_name: book.name,
    package_status: book.status,
    theme_color: book.theme_color?.trim() || 'theme-1',
    selling_price: Number.isFinite(sellingPrice) ? sellingPrice : null,
    total_wash_price: totalWashPrice || null,
    items: formItems,
  };
}

const WASH_ITEM_FIELD_KEYS = [
  'wash_type',
  'wash_name',
  'number_of_washes',
  'wash_price',
  'expiry_months',
] as const;

export function parseWashNameSelection(washNameValue: string): {
  packageId: string;
  variantKey?: string;
} {
  const separatorIndex = washNameValue.indexOf('::');
  if (separatorIndex === -1) {
    return { packageId: washNameValue };
  }
  return {
    packageId: washNameValue.slice(0, separatorIndex),
    variantKey: washNameValue.slice(separatorIndex + 2),
  };
}

export function resolveVariantId(
  variantKey: string | undefined,
  variantFromMeta?: { id?: string }
): string | undefined {
  if (!variantKey) {
    return undefined;
  }
  if (variantKey.startsWith('i:')) {
    const id = variantFromMeta?.id?.trim();
    return id || undefined;
  }
  return variantKey;
}

export function formatPrepaidExpiry(months: number): string {
  return `${months} months`;
}

function isFieldFilled(value: any): boolean {
  return value !== null && value !== undefined && value !== '';
}

export function isPrepaidWashItemEmpty(item: PrepaidWashItemForm): boolean {
  return WASH_ITEM_FIELD_KEYS.every(key => !isFieldFilled(item[key]));
}

export function isPrepaidWashItemComplete(item: PrepaidWashItemForm): boolean {
  return WASH_ITEM_FIELD_KEYS.every(key => isFieldFilled(item[key]));
}

export type ResolveVariantAtIndex = (
  index: number,
  washNameValue: string
) => { id?: string } | undefined;

export function mapPrepaidFormToCreatePayload(
  form: PrepaidPackageFormData,
  resolveVariantAtIndex: ResolveVariantAtIndex
): CreatePrepaidWashBookPayload {
  const items = (form.items ?? [])
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => isPrepaidWashItemComplete(item))
    .map(({ item, index }) => {
      const washName = item.wash_name as string;
      const { packageId, variantKey } = parseWashNameSelection(washName);
      const variantMeta = resolveVariantAtIndex(index, washName);
      const variantId = resolveVariantId(variantKey, variantMeta);

      const payloadItem: PrepaidWashBookItemPayload = {
        package_id: packageId,
        wash_type: item.wash_type as PrepaidWashType,
        expiry: formatPrepaidExpiry(Number(item.expiry_months)),
        wash_limit: Number(item.number_of_washes),
      };

      const price = item.wash_price != null ? Number(item.wash_price) : null;
      if (price != null && Number.isFinite(price)) {
        payloadItem.price = price;
      }
      if (variantId) {
        payloadItem.variant_id = variantId;
      }

      return payloadItem;
    });

  return {
    name: form.prepaid_package_name,
    status: form.package_status,
    selling_price: Number(form.selling_price),
    theme_color: form.theme_color?.trim() || DEFAULT_THEME_BACKGROUND,
    items,
  };
}

/** Line item on `GET package/prepaid-wash-books/purchases/{id}/status`. */
export interface PrepaidPurchaseStatusItem {
  id: string;
  sort_order?: number;
  package_id: string;
  type: string;
  is_add_on?: boolean;
  variant_id?: string | null;
  wash_limit?: number;
  limit: number;
  expiry?: string;
  price?: number | null;
  expires_at?: string | null;
  package_name: string;
  variant_name?: string | null;
  single_list_price?: number;
  purchase_item_id: string;
  redeemed_count: number;
  remaining: number;
  is_expired: boolean;
}

/** Purchase status line display name: "Package - Variant" when variant is set. */
export function formatPrepaidPurchaseItemDisplayName(
  item: Pick<PrepaidPurchaseStatusItem, 'package_name' | 'variant_name'>
): string {
  const packageName = item.package_name?.trim() || '';
  const variantName = item.variant_name?.trim() || '';
  if (packageName && variantName) {
    return `${packageName} - ${variantName}`;
  }
  return packageName || variantName || 'Wash';
}

/** Active prepaid purchase for order-request redemption UI. */
export interface PrepaidPurchaseStatus {
  prepaid_purchase_id: string;
  prepaid_wash_book_id: string;
  book_name: string;
  book_slug: string;
  customer_id: string;
  purchase_vehicle_id: string;
  purchase_order_id?: string;
  purchase_order_internal_id?: string;
  purchase_order_status?: string;
  purchase_payment_status?: string;
  /** When the washbook purchase order was created (status API). */
  purchase_created_at?: string;
  /** Alias / companion to purchase_created_at on some API versions. */
  order_created_at?: string;
  redemption_token: string;
  /** CWS-1908: the code read out when the QR cannot be scanned; null for a non-UUID token. */
  redemption_code?: string | null;
  items: PrepaidPurchaseStatusItem[];
}

/** Nested line on `GET package/prepaid-wash-books/purchases/{id}/redemptions`. */
export interface PrepaidPurchaseRedemptionItem {
  id: string;
  sort_order?: number;
  package_id: string;
  type: string;
  is_add_on?: boolean;
  variant_id?: string | null;
  wash_limit?: number;
  limit: number;
  expiry?: string;
  price?: number | null;
  expires_at?: string | null;
  package_name: string;
  variant_name?: string | null;
  single_list_price?: number;
  purchase_item_id: string;
}

/** Redemption row from `GET package/prepaid-wash-books/purchases/{id}/redemptions`. */
export interface PrepaidPurchaseRedemption {
  id: string;
  prepaid_purchase_id: string;
  prepaid_purchase_item_id: string;
  vehicle_id: string;
  redeemed_at: string;
  created_at?: string;
  updated_at?: string;
  redemption_order_internal_id?: string;
  redemption_order_id?: string;
  redemption_order_status?: string;
  redemption_order_payment_status?: string;
  item: PrepaidPurchaseRedemptionItem;
}

/** Default page size for prepaid purchase redemptions list. */
export const DEFAULT_PREPAID_PURCHASE_REDEMPTIONS_PAGE_LIMIT = 100;

/** Query params for prepaid purchase redemptions list. */
export interface PrepaidPurchaseRedemptionsListParams {
  page?: number;
  limit?: number;
}

/** Normalized redemptions list result. */
export interface PrepaidPurchaseRedemptionsListResult {
  redemptions: PrepaidPurchaseRedemption[];
  pagination: PrepaidWashBooksPagination;
}

/** Raw HTTP redemptions list body includes `pagination` beside `data`. */
export interface PrepaidPurchaseRedemptionsListApiResponse {
  status: boolean;
  status_code?: number;
  message?: string;
  data?: PrepaidPurchaseRedemption[];
  pagination?: PrepaidWashBooksPagination;
}

/** One wash slot to redeem on submit (`purchase_item_id` + 1-based `wash_index`). */
export interface PrepaidRedemptionItemPayload {
  purchase_item_id: string;
  wash_index: number;
}

/** Body for `POST package/prepaid-wash-books/purchases/{id}/redeem`. */
export interface RedeemPrepaidPurchasePayload {
  redemption_token: string;
  items: PrepaidRedemptionItemPayload[];
}
